<template>
    <div class="table-block" :class="{ 'with-heading': data.withHeadings }">
        <div v-for="(row, i) in data.content" :key="i" class="table-row">
            <a-dangerous-html
                v-for="(cell, j) in row"
                :key="j"
                class="table-cell"
                :content="cell"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import type { BodyVariant, TableData } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockTable',
});

defineProps<{
    id?: string;
    data: TableData;
    variant: BodyVariant;
}>();
</script>

<style lang="pcss" scoped>
.body-renderer .table-block {
    position: relative;
    height: 100%;
    display: grid;
    margin: 0 -16px 24px;
    overflow-x: scroll;

    @media (--viewport-tablet) {
        margin: 0 0 24px;
        overflow-x: auto;
    }

    &.with-heading {
        .table-row:first-child {
            @mixin lead-accent;

            z-index: 1;
            padding-bottom: 6px;
            color: var(--av-nav-primary);

            @media (--viewport-tablet) {
                padding-bottom: 0;
            }
        }
    }
}

.body-renderer .table-row {
    @mixin paragraph;

    display: flex;
    position: relative;
    padding-inline-start: 16px;

    @media (--viewport-tablet) {
        padding-inline-start: 0;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(10px, 1fr));
        column-gap: 16px;
        border-bottom: 1px solid var(--av-brand-secondary-light);
    }
}

.body-renderer .table-cell {
    flex: 0 0 calc(50vw - 8px);
    padding: 16px 16px;
    overflow: hidden;
    outline: none;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
        height: 1px;
        background: var(--av-brand-secondary-light);
    }

    &:first-child {
        padding-inline-start: 0;
    }

    &:last-child {
        &::after {
            inset-inline-end: 16px;
        }
    }

    @media (--viewport-tablet) {
        flex: none;
        padding: 24px 0;

        &::after {
            display: none;
        }
    }
}

.variant-blog .table-cell,
.variant-tru .table-cell {
    line-break: normal;
}

.variant-legal {
    .table-row,
    .table-cell {
        line-break: auto;
    }
}

.variant-resource .table-cell:deep(.centered) {
    display: block;
    text-align: center;
}
</style>
